import { z } from 'zod'
import { Environment, IsoDateTime } from './common'

export const RunStatus = z.enum([
  'queued',
  'running',
  'waiting',
  'succeeded',
  'failed',
  'timed_out',
  'cancelled',
])
export type RunStatus = z.infer<typeof RunStatus>

/** Who or what started a run (R11: a child run knows its parent). */
export const RunStarter = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('user'), userId: z.string() }),
  z.object({ kind: z.literal('workspace'), workspaceId: z.string(), runId: z.string(), nodeId: z.string() }),
  z.object({ kind: z.literal('webhook'), endpointId: z.string() }),
  z.object({ kind: z.literal('event'), subscriptionId: z.string(), eventName: z.string() }),
  z.object({
    kind: z.literal('page_action'),
    landingPageId: z.string(),
    actionId: z.string(),
    visitorId: z.string(),
  }),
  z.object({ kind: z.literal('schedule'), cron: z.string() }),
])
export type RunStarter = z.infer<typeof RunStarter>

export const Run = z.object({
  id: z.string(),
  workspaceId: z.string(),
  workflowId: z.string(),
  workflowSlug: z.string(),
  versionId: z.string(),
  versionNumber: z.number().int(),
  status: RunStatus,
  environment: Environment,
  startedBy: RunStarter,
  startedAt: IsoDateTime,
  endedAt: IsoDateTime.nullable(),
  durationMs: z.number().int().nullable(),
  outcome: z.string().nullable().describe('the End node’s outcome, or error / timed_out'),
  error: z.string().nullable(),
  input: z.record(z.string(), z.unknown()).describe('PII masked (P6)'),
  output: z.record(z.string(), z.unknown()).nullable(),
  parentRunId: z.string().nullable(),
  childRunIds: z.array(z.string()).default([]),
  simulated: z.boolean().default(false),
})
export type Run = z.infer<typeof Run>

export const RunStepStatus = z.enum([
  'pending',
  'running',
  'waiting',
  'succeeded',
  'failed',
  'skipped',
  'timed_out',
])
export type RunStepStatus = z.infer<typeof RunStepStatus>

export const RunStep = z.object({
  id: z.string(),
  runId: z.string(),
  index: z.number().int(),
  nodeId: z.string(),
  nodeType: z.string(),
  nodeName: z.string(),
  status: RunStepStatus,
  startedAt: IsoDateTime,
  endedAt: IsoDateTime.nullable(),
  durationMs: z.number().int().nullable(),
  outcome: z.string().nullable(),
  input: z.record(z.string(), z.unknown()),
  output: z.record(z.string(), z.unknown()).nullable(),
  error: z.string().nullable(),
  childRunId: z.string().nullable().default(null).describe('workflow nodes: the run they started'),
})
export type RunStep = z.infer<typeof RunStep>

export const RunFilters = z.object({
  workflowId: z.string().optional(),
  status: RunStatus.optional(),
  environment: Environment.optional(),
  startedByKind: RunStarter.options[0].shape.kind.or(z.string()).optional(),
  from: IsoDateTime.optional(),
  to: IsoDateTime.optional(),
  search: z.string().optional(),
})
export type RunFilters = z.infer<typeof RunFilters>

/** A run with its parents and children across workspaces. */
export const RunTreeNode: z.ZodType<RunTreeNodeType> = z.lazy(() =>
  z.object({
    run: Run,
    children: z.array(RunTreeNode),
  })
)
export interface RunTreeNodeType {
  run: Run
  children: RunTreeNodeType[]
}
